/**
 * <ProcessingUnitRoot> — renders modules + movers (+ mover-bound custom
 * assets and tools) for one XPU.
 *
 * Memoized so updates that don't touch the structural xpu reference
 * short-circuit. Mover-position ticks update through the
 * MoverPositionStore inside <XtsMover>'s useFrame, bypassing React.
 */

import React, { Suspense, useMemo } from 'react';
import type { ThreeEvent } from '@react-three/fiber';
import {
  buildChain,
  type BuiltChain,
} from '../../geometry/ChainBuilder.js';
import {
  isModuleSelected,
  isMoverSelected,
} from '../../interaction/SelectionManager.js';
import {
  resolveGuidingRailGlbUrl,
  resolveGuidingRailType,
  resolveModuleGlbUrl,
  resolveMoverGlbUrl,
} from '../../assets/AssetManifest.js';
import { composeAssetUrl } from '../../assets/AssetLoader.js';
import { getMoverEntry } from '../../geometry/MoverCatalog.js';
import { HepcoGfxRail } from './HepcoGfxRail.js';
import {
  resolveHepcoGfxProfile,
  type HepcoGfxRailProfileDims,
} from '../../geometry/HepcoGfxRailProfile.js';
import { XtsModule } from './XtsModule.js';
import { XtsMover } from './XtsMover.js';
import { XtsMoverTools } from './XtsMoverTools.js';
import { CustomAssetMesh } from './CustomAssetMesh.js';
import { PartTransformGroup } from './PartTransformGroup.js';
import { InstancedMoverBatch } from './InstancedMoverBatch.js';
import type { NormalizedXtsConfig } from '../../geometry/normalizeXtsConfig.js';
import type {
  AssetManifest,
  DisplayOptions,
  ModuleHighlight,
  ModuleRef,
  ModuleType3D,
  MoverRef,
  MoverToolType3D,
  MoverType3D,
  RailType3D,
  SelectionState,
} from '../../types.js';
import type { OriginCorrection } from '../../assets/SidecarLoader.js';

const DEG2RAD = Math.PI / 180;

export interface SceneSidecarOverrides {
  modules?: Partial<Record<ModuleType3D, OriginCorrection>>;
  movers?: Partial<Record<MoverType3D, OriginCorrection>>;
  tools?: Partial<Record<MoverToolType3D, OriginCorrection>>;
  rails?: Partial<Record<RailType3D, OriginCorrection>>;
}

interface ProcessingUnitRootProps {
  xpu: NormalizedXtsConfig['processingUnits'][number];
  assetsBaseUrl: string;
  assetManifest?: AssetManifest;
  display?: DisplayOptions;
  selection?: SelectionState;
  selectionColors?: { module?: string; mover?: string };
  onModuleClick?: (ref: ModuleRef, e: ThreeEvent<MouseEvent>) => void;
  onMoverClick?: (ref: MoverRef, e: ThreeEvent<MouseEvent>) => void;
  moduleHighlights?: ModuleHighlight[];
  sidecarOverrides?: SceneSidecarOverrides;
  /** All `customAssets` from the root config — mover-bound entries are
   *  rendered as children of <XtsMover> so they ride along through the
   *  imperative mover-position channel automatically. */
  customAssets?: NormalizedXtsConfig['customAssets'];
  /** Live override of the procedural Hepco-GFX rail profile dims. */
  hepcoGfxProfile?: Partial<HepcoGfxRailProfileDims>;
  /** Render mover bodies via InstancedMesh for reduced draw calls. */
  instancing?: boolean;
}

const ProcessingUnitRootImpl: React.FC<ProcessingUnitRootProps> = ({
  xpu,
  assetsBaseUrl,
  assetManifest,
  display,
  selection,
  selectionColors,
  onModuleClick,
  onMoverClick,
  moduleHighlights,
  sidecarOverrides,
  customAssets,
  hepcoGfxProfile,
  instancing = false,
}) => {
  const resolvedHepcoProfile = useMemo(
    () => resolveHepcoGfxProfile(hepcoGfxProfile),
    [hepcoGfxProfile],
  );
  // Build chains per part once per render. ChainBuilder is cheap.
  const chainsByPartOid = useMemo(() => {
    const map = new Map<number, BuiltChain>();
    for (const part of xpu.parts) {
      map.set(part.globalNumber, buildChain(part.modules));
    }
    return map;
  }, [xpu.parts]);

  const highlightByType = useMemo(() => {
    if (!moduleHighlights || moduleHighlights.length === 0) return null;
    const map = new Map<ModuleType3D, string>();
    for (const h of moduleHighlights) {
      for (const t of h.moduleTypes) {
        if (!map.has(t)) map.set(t, h.color);
      }
    }
    return map;
  }, [moduleHighlights]);

  const railSystem = xpu.railSystem ?? 'Beckhoff';
  const moverEntry = getMoverEntry(xpu.moverType);
  const moverGlbUrl = composeAssetUrl(
    assetsBaseUrl,
    resolveMoverGlbUrl(xpu.moverType, assetManifest?.movers),
  );

  return (
    <group name={`xpu-${xpu.objectId}`}>
      {/* Modules */}
      {xpu.parts.map((part) => {
        const chain = chainsByPartOid.get(part.globalNumber);
        if (!chain) return null;
        return (
          <PartTransformGroup
            key={part.globalNumber}
            name={`part-${part.globalNumber}`}
            partTransformation={part.partTransformation}
          >
            {railSystem === 'HepcoGfx' && (
              <HepcoGfxRail chain={chain} profile={resolvedHepcoProfile} />
            )}
            {chain.modules.map((built, idx) => {
              const filename = resolveModuleGlbUrl(
                built.moduleType,
                railSystem,
                assetManifest?.modules,
              );
              const url = filename
                ? composeAssetUrl(assetsBaseUrl, filename)
                : null;
              // Beckhoff guide rails are rendered as a separate GLB next to
              // each module. HepcoGfx swaps the rail look entirely (handled
              // in a later iteration) — for now skip the rail mesh.
              const railType =
                railSystem === 'Beckhoff'
                  ? resolveGuidingRailType(built.moduleType)
                  : null;
              const railGlbFile = railType
                ? resolveGuidingRailGlbUrl(
                    railType,
                    assetManifest?.guidingRails,
                  )
                : '';
              const guidingRail =
                railType && railGlbFile
                  ? {
                      railType,
                      glbUrl: composeAssetUrl(assetsBaseUrl, railGlbFile),
                    }
                  : null;
              const moduleRef: ModuleRef = {
                processingUnitObjectId: xpu.objectId,
                partObjectId: part.objectId,
                moduleIndex: idx,
              };
              const isSelected = selection
                ? isModuleSelected(selection, moduleRef)
                : false;
              const moduleHighlightColor =
                highlightByType?.get(built.moduleType) ?? undefined;
              return (
                <XtsModule
                  key={`${built.moduleType}-${idx}`}
                  module={built}
                  glbUrl={url}
                  assetsBaseUrl={assetsBaseUrl}
                  originCorrection={
                    sidecarOverrides?.modules?.[built.moduleType]
                  }
                  guidingRail={guidingRail}
                  guidingRailCorrection={
                    railType
                      ? sidecarOverrides?.rails?.[railType]
                      : undefined
                  }
                  selected={isSelected}
                  selectionColor={selectionColors?.module ?? '#FFE090'}
                  highlightColor={moduleHighlightColor}
                  {...(built.globalNumber !== undefined
                    ? { moduleGlobalNumber: built.globalNumber }
                    : {})}
                  driveStatus={built.status}
                  showWarnings={display?.showDriveWarnings !== false}
                  showErrors={display?.showDriveErrors !== false}
                  showCornerMarkers={
                    display?.showModuleCornerMarkers ?? false
                  }
                  cornerMarkerSizeMm={display?.moduleCornerMarkerSizeMm}
                  cornerMarkerColor={display?.moduleCornerMarkerColor}
                  onClick={
                    onModuleClick
                      ? (e) => onModuleClick(moduleRef, e)
                      : undefined
                  }
                />
              );
            })}
          </PartTransformGroup>
        );
      })}

      {/* Movers — instanced fast-path for scenes with many movers of
       *  the same type and no per-mover tools/assets. Falls back to
       *  individual rendering when selection, tools, or custom assets
       *  require per-mover scene nodes. */}
      {instancing && moverGlbUrl && xpu.movers.length >= 4 && (
        <Suspense fallback={null}>
          <InstancedMoverBatch
            glbUrl={moverGlbUrl}
            selectionColor={selectionColors?.mover ?? '#90E0FF'}
            movers={xpu.movers.map((m) => ({
              moverId: m.id,
              partPositionMm: m.partPositionMm,
              chain: chainsByPartOid.get(m.partOid)!,
              selected: selection
                ? isMoverSelected(selection, {
                    processingUnitObjectId: xpu.objectId,
                    moverIndex: m.index,
                  })
                : false,
              positionFrame: xpu.positionFrame,
            })).filter((m) => m.chain !== undefined)}
          />
        </Suspense>
      )}
      <Suspense fallback={null}>
        {xpu.movers.map((m) => {
          const chain = chainsByPartOid.get(m.partOid);
          if (!chain) return null;
          // Apply same partTransformation as modules so movers stay aligned.
          const part = xpu.parts.find((p) => p.globalNumber === m.partOid);
          // When instancing handles the body, only render if this mover
          // has tools, custom assets, or needs per-mover event handling.
          const hasChildren =
            (m.tools && m.tools.length > 0) ||
            (customAssets ?? []).some(
              (a) =>
                a.visible !== false &&
                (a.binding.type === 'all-movers' ||
                  (a.binding.type === 'mover' &&
                    a.binding.moverRef.processingUnitObjectId === xpu.objectId &&
                    a.binding.moverRef.moverIndex === m.index)),
            );
          const skipBody =
            instancing && moverGlbUrl && xpu.movers.length >= 4 && !hasChildren && !onMoverClick;
          if (skipBody) return null;
          return (
            <PartTransformGroup
              key={m.id}
              name={`part-${m.partOid}-mover-${m.id}`}
              partTransformation={part?.partTransformation}
            >
              <XtsMover
                chain={chain}
                partPositionMm={m.partPositionMm}
                moverId={m.id}
                glbUrl={moverGlbUrl}
                moverType={xpu.moverType}
                assetsBaseUrl={assetsBaseUrl}
                originCorrection={sidecarOverrides?.movers?.[xpu.moverType]}
                pathLengthMm={moverEntry?.pathLengthMm}
                heightMm={moverEntry?.heightMm}
                {...(xpu.positionFrame
                  ? { positionFrame: xpu.positionFrame }
                  : {})}
                selected={
                  selection
                    ? isMoverSelected(selection, {
                        processingUnitObjectId: xpu.objectId,
                        moverIndex: m.index,
                      })
                    : false
                }
                selectionColor={selectionColors?.mover ?? '#90E0FF'}
                driveStatus={m.status}
                showWarnings={display?.showDriveWarnings !== false}
                showErrors={display?.showDriveErrors !== false}
                showMoverId={display?.showMoverIds === true}
                {...(display?.moverIdLabelOptions
                  ? { moverIdLabelOptions: display.moverIdLabelOptions }
                  : {})}
                onClick={
                  onMoverClick
                    ? (e) =>
                        onMoverClick(
                          {
                            processingUnitObjectId: xpu.objectId,
                            moverIndex: m.index,
                          },
                          e,
                        )
                    : undefined
                }
              >
                {m.tools && m.tools.length > 0 ? (
                  <XtsMoverTools
                    tools={m.tools}
                    assetsBaseUrl={assetsBaseUrl}
                    assetManifest={assetManifest}
                    toolOverrides={sidecarOverrides?.tools}
                  />
                ) : null}
                {(customAssets ?? []).map((asset) => {
                  if (asset.visible === false) return null;
                  const isAllMovers = asset.binding.type === 'all-movers';
                  const isThisMover =
                    asset.binding.type === 'mover' &&
                    asset.binding.moverRef.processingUnitObjectId === xpu.objectId &&
                    asset.binding.moverRef.moverIndex === m.index;
                  if (!isAllMovers && !isThisMover) return null;
                  const binding = asset.binding as {
                    offsetMm?: [number, number, number];
                    rotationDegEuler?: [number, number, number];
                    scale?: number;
                  };
                  return (
                    <CustomAssetMesh
                      key={asset.id}
                      glbUrl={asset.glbUrl}
                      positionMm={binding.offsetMm ?? [0, 0, 0]}
                      rotationDegEuler={binding.rotationDegEuler ?? [0, 0, 0]}
                      scale={binding.scale ?? 1}
                      opacity={asset.opacity}
                    />
                  );
                })}
              </XtsMover>
            </PartTransformGroup>
          );
        })}
      </Suspense>
    </group>
  );
};

export const ProcessingUnitRoot = React.memo(ProcessingUnitRootImpl);
ProcessingUnitRoot.displayName = 'ProcessingUnitRoot';
